import { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Minus, Plus, ShoppingCart } from 'lucide-react';
import { priceFor, inr, QUALITY_LABELS, imgUrl } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';

export default function ProductCard({ product, onOpen }) {
    const { addItem, globalQuality } = useCart();
    const [quality, setQuality] = useState(globalQuality);
    const [qty, setQty] = useState(1);
    // Sync with global quality changes when user picks a new global tier
    useEffect(() => { setQuality(globalQuality); }, [globalQuality]);
    const price = priceFor(product, quality);

    return (
        <article
            data-testid={`product-card-${product.sku}`}
            className="group card-flat flex flex-col hover-lift"
        >
            <button onClick={() => onOpen?.(product)} className="block relative aspect-square bg-white overflow-hidden border-b border-slate-100" data-testid={`product-image-${product.sku}`}>
                <img
                    src={imgUrl(product.image_url)}
                    alt={product.name}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = 'data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 200 200%22><rect width=%22200%22 height=%22200%22 fill=%22%23FFFFFF%22/><text x=%2250%25%22 y=%2250%25%22 text-anchor=%22middle%22 dy=%22.35em%22 font-family=%22Outfit%22 font-size=%2214%22 fill=%22%23475569%22>' + encodeURIComponent(product.sku) + '</text></svg>'; }}
                    className="w-full h-full object-contain p-3 transition-transform duration-500 group-hover:scale-105"
                />
            </button>

            <div className="p-4 flex flex-col gap-3 flex-1">
                <div>
                    <h3 className="font-display font-semibold text-slate-900 text-sm leading-snug line-clamp-2">{product.name}</h3>
                    <div className="text-[11px] text-slate-500 mt-1">{product.size}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Quality: {QUALITY_LABELS[quality]}</div>
                </div>

                <div className="flex items-end justify-between">
                    <div>
                        <div className="text-[9px] uppercase tracking-wider text-slate-500">Price</div>
                        <div className="font-display text-xl font-bold text-blue-900">{inr(price)}</div>
                    </div>
                    <Select value={quality} onValueChange={setQuality}>
                        <SelectTrigger className="w-24 h-8 rounded-none text-xs" data-testid={`quality-select-${product.sku}`}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="eco">Eco</SelectItem>
                            <SelectItem value="prime">Prime</SelectItem>
                            <SelectItem value="gold">Gold</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex items-center justify-between gap-2 mt-auto">
                    <div className="flex items-center border border-slate-300">
                        <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-7 h-8 flex items-center justify-center hover:bg-slate-100" data-testid={`qty-minus-${product.sku}`}>
                            <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-7 text-center text-xs font-semibold" data-testid={`qty-value-${product.sku}`}>{qty}</span>
                        <button onClick={() => setQty(qty + 1)} className="w-7 h-8 flex items-center justify-center hover:bg-slate-100" data-testid={`qty-plus-${product.sku}`}>
                            <Plus className="w-3 h-3" />
                        </button>
                    </div>
                    <button
                        data-testid={`add-to-cart-${product.sku}`}
                        onClick={() => { addItem(product, qty, quality); toast.success(`${product.name} added`); }}
                        className="flex-1 bg-amber-600 text-white text-xs font-semibold px-3 py-2 hover:bg-amber-700 transition-colors flex items-center justify-center gap-1.5"
                    >
                        <ShoppingCart className="w-3.5 h-3.5" /> Add
                    </button>
                </div>
            </div>
        </article>
    );
}
